import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { FileDown, Grip, Tally2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PStaffTable from "./p-staff-table";
import Paginat from "@/components/lib/paginat";
import { useSelector, useDispatch } from "react-redux";
import { TabsContent } from "@/components/ui/tabs";
import { fetchPayrollStaffList } from "@/redux/slice/staff-management/staff-attendance";
import useAuth from "@/hooks/useAuth";
import useDebounce from "@/hooks/useDebounce";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";

function StaffTabs() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const restaurant_id = user?.restaurant_id || 1;

  const { payrollStaffs, payrollStaffPagination, payrollStaffLoading } = useSelector(
    (state) => state.staffManage.attendance
  );

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [searchInput, setSearchInput] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    dispatch(
      fetchPayrollStaffList({
        restaurant_id,
        month: selectedMonth,
        year: selectedYear,
        per_page: perPage,
        search_term: debouncedSearch,
        page: currentPage,
        sort_field: "id",
        sort_order: "desc",
      })
    );
  }, [dispatch, restaurant_id, selectedMonth, selectedYear, perPage, debouncedSearch, currentPage]);

  return (
    <TabsContent className="mt-5" value={"staff"}>
      <Card className="bg-cblack3 py-2 border-0">
        <CardContent className="flex justify-between flex-col gap-5 items-center mt-4">
          <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full lg:w-auto">
              <div className="grid w-full sm:w-auto items-center gap-1.5">
                <Label className="text-sm text-cwhite2" htmlFor="staff-search">
                  Staff ID/Name
                </Label>
                <Input
                  className="text-gray-300 w-full sm:w-[180px]"
                  type="text"
                  id="staff-search"
                  value={searchInput}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setSearchInput(e.target.value);
                  }}
                  placeholder="Search by Staff ID"
                />
              </div>
              <div className="grid w-full sm:w-auto items-center gap-1.5">
                <Label className="text-sm text-cwhite2" htmlFor="month-select">
                  Month
                </Label>
                <Select
                  value={selectedMonth}
                  onValueChange={(val) => {
                    setCurrentPage(1);
                    setSelectedMonth(val);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="1">January</SelectItem>
                      <SelectItem value="2">February</SelectItem>
                      <SelectItem value="3">March</SelectItem>
                      <SelectItem value="4">April</SelectItem>
                      <SelectItem value="5">May</SelectItem>
                      <SelectItem value="6">June</SelectItem>
                      <SelectItem value="7">July</SelectItem>
                      <SelectItem value="8">August</SelectItem>
                      <SelectItem value="9">September</SelectItem>
                      <SelectItem value="10">October</SelectItem>
                      <SelectItem value="11">November</SelectItem>
                      <SelectItem value="12">December</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid w-full sm:w-auto items-center gap-1.5">
                <Label className="text-sm text-cwhite2" htmlFor="year-select">
                  Year
                </Label>
                <Select
                  value={selectedYear}
                  onValueChange={(val) => {
                    setCurrentPage(1);
                    setSelectedYear(val);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-[120px]">
                    <SelectValue placeholder="2025" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2027">2027</SelectItem>
                      <SelectItem value="2028">2028</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5 justify-end items-end">
              <h3 className="text-sm font-semibold text-color7">
                Total Staff: {payrollStaffPagination?.total || 0}
              </h3>
              <div className="flex gap-3 justify-center items-center">
                <Button className="bg-cblack5 text-white border border-gray-700">
                  <FileDown /> Export
                </Button>
                <span className="text-white">
                  <Grip size={26} />
                </span>
                <span className="text-cyellow rotate-90 mt-2">
                  <Tally2 size={25} />
                </span>
                <div className="flex gap-1 justify-center items-center">
                  <h3 className="text-sm text-cwhite2">Show</h3>
                  <Select
                    value={perPage.toString()}
                    onValueChange={(val) => {
                      setCurrentPage(1);
                      setPerPage(Number(val));
                    }}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue placeholder="10" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full">
            {payrollStaffLoading ? (
              <OnlyTableSekeleton header={false} row={perPage} columns={9} />
            ) : (
              <PStaffTable data={payrollStaffs} restaurantId={restaurant_id} />
            )}
          </div>
        </CardContent>
      </Card>
      <Paginat
        className={"mt-4"}
        pagination={payrollStaffPagination}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </TabsContent>
  );
}

export default StaffTabs;
